import { useState, type FormEvent } from 'react';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/dah8nhjoh653ivfvnogg';

type Status = 'idle' | 'loading' | 'success' | 'error';

interface ParsedResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

export default function ContactForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');
  const [successText, setSuccessText] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const guard = String(formData.get('website_alt') ?? '').trim();

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'website_alt') return;
      const text = typeof value === 'string' ? value.trim() : '';
      if (text === '') return;
      payload.append(key, text);
    });

    if (guard !== '') {
      setStatus('success');
      setSuccessText('已收到你的推荐，我们会尽快评估这部剧的更新节奏。');
      form.reset();
      return;
    }

    setStatus('loading');
    setFormError('');
    setSuccessText('');

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: ParsedResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as ParsedResponse;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');

      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setStatus('success');
        setSuccessText('已收到你的推荐，我们会尽快评估这部剧的更新节奏。');
        form.reset();
        return;
      }

      setStatus('error');
      setFormError(serverMsg || '提交没有成功，请稍后重试，或直接发邮件给我们。');
    } catch {
      setStatus('error');
      setFormError('网络连接似乎不太稳定，请稍后重试，或直接发邮件给我们。');
    }
  };

  return (
    <form
      data-readdy-form
      id="contact-form"
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-2">
          <label htmlFor="contact-name" className="text-sm text-foreground-700">
            你的称呼
          </label>
          <input
            id="contact-name"
            name="name"
            type="text"
            required
            placeholder="怎么称呼你"
            className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
          />
        </div>
        <div className="flex flex-col gap-2">
          <label htmlFor="contact-email" className="text-sm text-foreground-700">
            邮箱
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            placeholder="便于我们回复你"
            className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
          />
        </div>
      </div>

      <div className="mt-4 flex flex-col gap-2">
        <label htmlFor="contact-drama" className="text-sm text-foreground-700">
          想推荐的短剧
        </label>
        <input
          id="contact-drama"
          name="drama"
          type="text"
          placeholder="剧名 + 播出平台，例如《长安十二娘》"
          className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
        />
      </div>

      <div className="mt-4 flex flex-col gap-2">
        <label htmlFor="contact-message" className="text-sm text-foreground-700">
          想说的话
        </label>
        <textarea
          id="contact-message"
          name="message"
          rows={4}
          maxLength={500}
          placeholder="可以写下推荐理由，或者你遇到的追剧顺序问题（最多 500 字）"
          className="resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm leading-6 text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
        />
      </div>

      <input
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="form-guard-field"
      />

      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs leading-5 text-foreground-500">
          我们只用于回复你的推荐，不会用于其他用途。
        </p>
        <button
          type="submit"
          disabled={status === 'loading'}
          className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
        >
          <i className={status === 'loading' ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-line'} aria-hidden="true" />
          {status === 'loading' ? '提交中…' : '提交推荐'}
        </button>
      </div>

      {status === 'success' && (
        <p className="mt-4 flex items-center gap-2 rounded-md bg-accent-50 px-3 py-2.5 text-sm text-accent-900">
          <i className="ri-checkbox-circle-line" aria-hidden="true" />
          {successText}
        </p>
      )}
      {status === 'error' && (
        <p className="mt-4 flex items-center gap-2 rounded-md bg-primary-50 px-3 py-2.5 text-sm text-primary-700">
          <i className="ri-error-warning-line" aria-hidden="true" />
          {formError}
        </p>
      )}
    </form>
  );
}